import { AnalysisAssetItem, AnalysisAssetsState, AnalysisAssetType, AssetLookup } from '../types';
import { sortStates } from '../../../utils/sortStates';

type RawAnalysisAsset = {
  name: string;
  state?: string;
  selectedImageUrl?: string;
  episodes?: number[];
};

type ScriptAnalysisAssets = {
  characters?: string | RawAnalysisAsset[];
  scenes?: string | RawAnalysisAsset[];
  props?: string | RawAnalysisAsset[];
};

const BASIC_STATE = 'basic';

function isBasicState(state: string | undefined): boolean {
  return (state || '') === BASIC_STATE;
}

function parseList(value: ScriptAnalysisAssets[keyof ScriptAnalysisAssets]): RawAnalysisAsset[] {
  if (!value) return [];
  return typeof value === 'string' ? JSON.parse(value || '[]') : value;
}

function buildItems(type: AnalysisAssetType, list: RawAnalysisAsset[]): AnalysisAssetItem[] {
  const nameMap: Record<string, AnalysisAssetItem> = {};

  list.forEach(item => {
    const imageUrl = item.selectedImageUrl || '';
    const state = item.state || '';
    const basic = isBasicState(item.state);
    if (!nameMap[item.name]) {
      nameMap[item.name] = { type, name: item.name, state, imageUrl, allStates: [], hasImage: !!imageUrl };
    }
    if (imageUrl) {
      const entry = nameMap[item.name];
      if (!entry.hasImage || (isBasicState(entry.state) && !basic)) {
        entry.imageUrl = imageUrl;
        entry.state = state;
        entry.hasImage = true;
      }
      if (!basic) {
        entry.allStates.push({ state, imageUrl, episodes: item.episodes });
      }
    }
  });

  return Object.values(nameMap).map(entry => ({ ...entry, allStates: sortStates(entry.allStates) }));
}

function lookupKey(type: AnalysisAssetType, name: string): string {
  return `${type}\0${name.trim().toLowerCase()}`;
}

function buildLookup(groups: Array<{ type: AnalysisAssetType; list: RawAnalysisAsset[] }>): AssetLookup {
  const lookup: AssetLookup = new Map();

  groups.forEach(({ type, list }) => {
    list.forEach(item => {
      const key = lookupKey(type, item.name);
      if (!lookup.has(key)) {
        lookup.set(key, { type, name: item.name, stateMap: new Map(), allStates: [] });
      }
      const entry = lookup.get(key)!;
      const state = item.state || '';
      const imageUrl = item.selectedImageUrl || '';
      entry.stateMap.set(state, imageUrl);
      if (!isBasicState(item.state)) {
        entry.allStates.push({ state, imageUrl, episodes: item.episodes });
      }
    });
  });

  lookup.forEach((entry, key) => {
    lookup.set(key, { ...entry, allStates: sortStates(entry.allStates) });
  });

  return lookup;
}

export function findAssetLookupEntry(
  lookup: AssetLookup,
  ref: { name: string; type?: string; state?: string; imageUrl?: string },
): { type: AnalysisAssetType; entry: NonNullable<ReturnType<AssetLookup['get']>> } | undefined {
  if (ref.type === 'character' || ref.type === 'scene' || ref.type === 'prop') {
    const entry = lookup.get(lookupKey(ref.type, ref.name));
    return entry ? { type: ref.type, entry } : undefined;
  }

  const normalizedName = ref.name.trim().toLowerCase();
  const state = ref.state || '';
  const matches: Array<{ type: AnalysisAssetType; entry: NonNullable<ReturnType<AssetLookup['get']>> }> = [];
  lookup.forEach(entry => {
    if (entry.name.trim().toLowerCase() === normalizedName) {
      matches.push({ type: entry.type, entry });
    }
  });
  if (matches.length === 0) return undefined;

  if (ref.imageUrl) {
    const bySnapshot = matches.find(match => match.entry.stateMap.get(state) === ref.imageUrl);
    if (bySnapshot) return bySnapshot;
  }
  if (matches.length === 1) return matches[0];
  return undefined;
}

export function parseAnalysisAssets(analysis: ScriptAnalysisAssets): {
  assets: AnalysisAssetsState;
  lookup: AssetLookup;
} {
  const characters = parseList(analysis.characters);
  const scenes = parseList(analysis.scenes);
  const props = parseList(analysis.props);

  return {
    assets: {
      characters: buildItems('character', characters),
      scenes: buildItems('scene', scenes),
      props: buildItems('prop', props),
    },
    lookup: buildLookup([
      { type: 'character', list: characters },
      { type: 'scene', list: scenes },
      { type: 'prop', list: props },
    ]),
  };
}
